import {
  displayServerMask,
  formatCount,
  formatLocalDate,
  formatRatePercent,
  formatTimestamp,
  formatToman,
  statusLabel,
} from '@saay/formatting';
import {
  CopyableIdentifier,
  type DetailItem,
  FactList,
  Ltr,
  Masked,
  Money,
  Table,
} from '../kit.js';
import type { ReactNode } from 'react';

import { AbnormalStatusChip } from './admin-feedback.js';
import type {
  ApprovalImpactReport,
  ImpactFinancialAdjustment,
  PlatformRoleGrantImpact,
  TargetingSnapshot,
  ValidityPolicy,
} from '../api.js';
import { formatProgramCodeRevision, shortReference } from '../display.js';
import { formatDays } from '../format.js';
import { SnapshotFacts } from './targeting-snapshot.js';

/**
 * The approval request detail (AC §18.2.5, §18.2.7, FTD §9.2): the stored impact report and the stored
 * command (`payload`), exactly as the server returns them. Nothing is recomputed: a before → after
 * pair shows the two server values, never a difference the client derives.
 */

const LOCAL_DATE = /^\d{4}-\d{2}-\d{2}$/;
const TIMESTAMP = /^\d{4}-\d{2}-\d{2}T/;
const MASKED_KEY = /masked|sheba|mobile|nationalCode/i;

/** Persian labels of the payload and impact fields the Stage 3 action types store. */
const FIELD_LABELS: Readonly<Record<string, string>> = {
  allocationId: 'تخصیص اعتبار',
  availableAfterIrr: 'ظرفیت آزاد پس از تغییر',
  buyerId: 'خریدار',
  changeRequestId: 'درخواست تغییر',
  committedIrr: 'تعهدشده',
  currentLimitIrr: 'سقف فعلی',
  currentSaayFeeRate: 'نرخ فعلی SAAY',
  dedicatedOrganizationId: 'سازمان اختصاصی',
  defaultAmountIrr: 'مبلغ پیش‌فرض',
  deltaIrr: 'تغییر سقف',
  effectiveFrom: 'تاریخ اجرا',
  expectedVersion: 'نسخهٔ مبنا',
  holderName: 'صاحب حساب',
  initial: 'اولین تنظیم',
  installmentBounds: 'محدودهٔ اقساط فروشگاه',
  maxIntervalDays: 'بیشترین فاصلهٔ اقساط',
  maxTotalDurationDays: 'بیشترین مدت کل',
  merchantId: 'فروشگاه',
  minIntervalDays: 'کمترین فاصلهٔ اقساط',
  newApprovedLimitIrr: 'سقف مصوب جدید',
  newHolderName: 'صاحب حساب جدید',
  newLimitIrr: 'سقف جدید',
  newSaayFeeRate: 'نرخ جدید SAAY',
  newShebaMasked: 'شبای جدید',
  outstandingPrincipalIrr: 'اصل بدهی فعال',
  ownerDisplayNameFa: 'صاحب حساب تسویه',
  ownerId: 'صاحب حساب',
  ownerType: 'نوع صاحب حساب',
  programCode: 'کد برنامه',
  programId: 'برنامه اعتباری',
  reason: 'دلیل',
  reservedPrincipalIrr: 'رزرو موقت',
  revision: 'بازنگری',
  role: 'نقش',
  runId: 'اجرای هدف‌گذاری',
  saayFeeRate: 'نرخ SAAY',
  sheba: 'شبا',
  shebaMasked: 'شبا',
  source: 'منشأ',
  supersedesProgramId: 'جایگزین برنامه',
  totalExposureCapIrr: 'سقف کل تعهد',
  unsettledPrincipalIrr: 'اصل بدهی تسویه‌نشده',
  userId: 'کاربر',
};

export function storedFieldLabel(key: string): ReactNode {
  return FIELD_LABELS[key] ?? <Ltr>{key}</Ltr>;
}

/** One stored value, formatted by its kind (rials, rate, date, timestamp, mask) without arithmetic. */
export function StoredValue({ name, value }: { readonly name: string; readonly value: unknown }) {
  if (value === null || value === undefined) {
    return <span className="admin-muted">—</span>;
  }
  if (typeof value === 'boolean') {
    return <>{value ? 'بله' : 'خیر'}</>;
  }
  if (typeof value === 'number') {
    if (name.endsWith('Irr') && Number.isSafeInteger(value)) {
      return <Money rials={value} />;
    }
    if (name.endsWith('Days')) {
      return <>{formatDays(value)}</>;
    }
    return <>{formatCount(value)}</>;
  }
  if (typeof value === 'string') {
    if (name.endsWith('Rate')) {
      return <>{formatRatePercent(value, { invalid: 'raw' })}</>;
    }
    if (name === 'role') {
      return <>{statusLabel('fixedRole', value)}</>;
    }
    if (LOCAL_DATE.test(value)) {
      return <>{formatLocalDate(value)}</>;
    }
    if (TIMESTAMP.test(value)) {
      return <time dateTime={value}>{formatTimestamp(value)}</time>;
    }
    if (MASKED_KEY.test(name)) {
      return <Masked kind={/mobile/i.test(name) ? 'mobile' : 'text'} value={value} />;
    }
    if (name.endsWith('Id')) {
      return <CopyableIdentifier label={<Ltr>{shortReference(value)}</Ltr>} value={value} />;
    }
    if (/^[A-Z][A-Z0-9_]+$/.test(value)) {
      return <>{'مقدار ثبت‌شده'}</>;
    }
    return <>{displayServerMask(value)}</>;
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? (
      <span className="admin-muted">—</span>
    ) : (
      <ul className="admin-plain-list">
        {value.map((item, index) => (
          <li key={index}>
            <StoredValue name={name} value={item} />
          </li>
        ))}
      </ul>
    );
  }
  if (typeof value === 'object') {
    return <StoredFields fields={value as Record<string, unknown>} />;
  }
  return <Ltr>{String(value)}</Ltr>;
}

/** A stored object (the payload, or an impact report the client has no layout for) as rows. */
function isEmptyStoredValue(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

export function StoredFields({ fields }: { readonly fields: Readonly<Record<string, unknown>> }) {
  const items: DetailItem[] = Object.entries(fields)
    .filter(([key, value]) => key !== 'actionType' && !isEmptyStoredValue(value))
    .map(([key, value]) => ({
      key,
      label: storedFieldLabel(key),
      value: <StoredValue name={key} value={value} />,
    }));
  return items.length === 0 ? <span className="admin-muted">—</span> : <FactList items={items} />;
}

/**
 * A grant of an approver role (D-49, AC §18.20.2): who receives which role, the grantee's standing at
 * request time, and the Super Admin count. The mobile is masked by the server and shown with •.
 */
function RoleGrantImpact({ report }: { readonly report: PlatformRoleGrantImpact }) {
  const { grantee } = report;
  return (
    <FactList
      items={[
        { key: 'role', label: 'نقش درخواستی', value: statusLabel('fixedRole', report.role) },
        {
          key: 'grantee',
          label: 'دریافت‌کننده',
          value: (
            <span className="admin-inline">
              {grantee.displayNameFa ?? 'بدون نام'}
              <Masked kind="mobile" value={grantee.mobileMasked} />
            </span>
          ),
        },
        {
          key: 'identity',
          label: 'حساب کاربر',
          value: grantee.newIdentity
            ? 'کاربر جدید؛ حساب ورود پس از تأیید ساخته می‌شود'
            : statusLabel('identityUserStatus', grantee.identityStatus),
        },
        {
          key: 'roles',
          label: 'نقش‌های فعال فعلی',
          value:
            grantee.activePlatformRoles.length === 0
              ? 'ندارد'
              : grantee.activePlatformRoles
                  .map((role) => statusLabel('fixedRole', role))
                  .join('، '),
        },
        {
          key: 'merchant',
          label: 'کارمند فروشگاه',
          value: grantee.holdsMerchantAssignment ? 'بله' : 'خیر',
        },
        { key: 'buyer', label: 'خریدار', value: grantee.isBuyer ? 'بله' : 'خیر' },
        {
          key: 'supers',
          label: 'مدیران ارشد فعال',
          value: formatCount(report.activeSuperAdminCount),
        },
      ]}
    />
  );
}

/** Ledger account labels of the financial adjustment postings (LEDGER §2, §18). */
const ACCOUNT_LABELS: Readonly<Record<string, string>> = {
  BUYER_PRINCIPAL_RECEIVABLE: 'اصل مطالبه از خریدار',
  FINANCIAL_ADJUSTMENT_EXPENSE: 'هزینهٔ اصلاح مالی',
  FINANCIAL_ADJUSTMENT_REVENUE: 'درآمد اصلاح مالی',
};

function accountLabel(code: string): ReactNode {
  return ACCOUNT_LABELS[code] ?? 'سرفصل دیگر';
}

/**
 * A `FINANCIAL_ADJUSTMENT` (AC §18.11): the balance before and after and the exact journal lines the
 * approval posts, from the stored impact report. Accounting only (`operationalEffects` is empty).
 */
function installmentFactLabel(
  report: ImpactFinancialAdjustment,
): { label: string; value: ReactNode } | null {
  if (report.saleReference === null || report.installmentSequence === null) {
    return null;
  }
  return {
    label: 'قسط',
    value: `${report.saleReference} — قسط ${formatCount(report.installmentSequence)}`,
  };
}

function FinancialAdjustmentImpact({ report }: { readonly report: ImpactFinancialAdjustment }) {
  const installment = installmentFactLabel(report);
  return (
    <>
      <FactList
        items={[
          { key: 'account', label: 'حساب', value: accountLabel(report.accountCode) },
          {
            key: 'direction',
            label: 'جهت',
            value: statusLabel('financialAdjustmentDirection', report.direction),
          },
          { key: 'amount', label: 'مبلغ اصلاح', value: <Money rials={report.amountIrr} /> },
          {
            key: 'balance',
            label: 'مانده',
            value: change(money(report.currentBalanceIrr), money(report.balanceAfterIrr)),
          },
          { key: 'source', label: 'مستند منبع', value: report.sourceReference },
          {
            key: 'effects',
            label: 'اثر عملیاتی',
            value:
              report.operationalEffects.length === 0
                ? 'ندارد؛ قرارداد، اقساط و تخصیص تغییر نمی‌کنند'
                : report.operationalEffects.join('، '),
          },
          ...(installment === null ? [] : [{ key: 'installment', ...installment }]),
        ]}
      />
      <Table
        caption="سطرهای سند حسابداری"
        columns={[
          {
            header: 'حساب',
            key: 'account',
            pin: 'start',
            render: (row) => accountLabel(row.account),
          },
          {
            header: 'بدهکار',
            key: 'debit',
            numeric: true,
            render: (row) => (row.direction === 'DEBIT' ? <Money rials={row.amountIrr} /> : '—'),
          },
          {
            header: 'بستانکار',
            key: 'credit',
            numeric: true,
            pin: 'end',
            render: (row) => (row.direction === 'CREDIT' ? <Money rials={row.amountIrr} /> : '—'),
          },
        ]}
        rowKey={(row) => `${row.account}:${row.direction}`}
        rows={report.postings}
      />
    </>
  );
}

function validityPolicyText(policy: ValidityPolicy | Readonly<Record<string, unknown>>): string {
  const mode = (policy as { mode?: unknown }).mode;
  if (mode === 'DURATION_FROM_ASSIGNMENT') {
    const value = (policy as { value?: unknown }).value;
    const unit = (policy as { unit?: unknown }).unit;
    return `${typeof value === 'number' ? formatCount(value) : '—'} ${unit === 'MONTH' ? 'ماه' : 'روز'} از تخصیص`;
  }
  if (mode === 'FIXED_WINDOW') {
    const from = (policy as { validFrom?: unknown }).validFrom;
    const until = (policy as { validUntil?: unknown }).validUntil;
    return typeof from === 'string' && typeof until === 'string'
      ? `${formatTimestamp(from, { withTime: false })} تا ${formatTimestamp(until, { withTime: false })}`
      : statusLabel('validityMode', 'FIXED_WINDOW');
  }
  return typeof mode === 'string' ? statusLabel('validityMode', mode) : '—';
}

/** «۲۰۰ میلیون → ۳۵۰ میلیون» as two server amounts (exact toman, never a client difference). */
function change(from: string, to: string): string {
  return `${from} ← ${to}`;
}

function money(rials: number): string {
  return formatToman(rials);
}

/**
 * The impact report of the request (AC §18.2.5): a layout per known action type, and the stored
 * fields as rows for any other (additive) action type, so a new type renders without a release.
 */
export function ImpactReport({ report }: { readonly report: ApprovalImpactReport }) {
  switch (report.actionType) {
    case 'MERCHANT_CAPACITY_CHANGE':
      return (
        <FactList
          items={[
            {
              key: 'limit',
              label: 'سقف ظرفیت',
              value: change(money(report.currentLimitIrr), money(report.newLimitIrr)),
            },
            {
              key: 'unsettled',
              label: 'اصل بدهی تسویه‌نشده',
              value: <Money rials={report.unsettledPrincipalIrr} />,
            },
            {
              key: 'reserved',
              label: 'رزرو موقت',
              value: <Money rials={report.reservedPrincipalIrr} />,
            },
            {
              key: 'available',
              label: 'ظرفیت آزاد پس از تأیید',
              value: <Money rials={report.availableAfterIrr} />,
            },
            {
              key: 'initial',
              label: 'نوع تغییر',
              value: report.initial ? 'ظرفیت اولیهٔ فروشگاه' : 'تغییر ظرفیت مصوب',
            },
          ]}
        />
      );
    case 'MERCHANT_FEE_CHANGE':
      return (
        <FactList
          items={[
            {
              key: 'rate',
              label: 'نرخ SAAY',
              value:
                report.currentSaayFeeRate === null
                  ? `اولین کارمزد: ${formatRatePercent(report.newSaayFeeRate, { invalid: 'raw' })}`
                  : change(
                      formatRatePercent(report.currentSaayFeeRate, { invalid: 'raw' }),
                      formatRatePercent(report.newSaayFeeRate, { invalid: 'raw' }),
                    ),
            },
            {
              key: 'from',
              label: 'تاریخ اجرا',
              value: formatLocalDate(report.effectiveFrom, 'long'),
            },
            {
              key: 'bounds',
              label: 'محدودهٔ اقساط فروشگاه',
              value:
                report.installmentBounds === null ? (
                  'فقط محدودهٔ پلتفرم'
                ) : (
                  <StoredFields fields={report.installmentBounds} />
                ),
            },
          ]}
        />
      );
    case 'BUYER_CREDIT_CHANGE':
      return (
        <FactList
          items={[
            {
              key: 'limit',
              label: 'اعتبار پایه',
              value: change(money(report.currentLimitIrr), money(report.newLimitIrr)),
            },
            {
              key: 'outstanding',
              label: 'اصل بدهی فعال',
              value: <Money rials={report.outstandingPrincipalIrr} />,
            },
            {
              key: 'reserved',
              label: 'رزرو موقت',
              value: <Money rials={report.reservedPrincipalIrr} />,
            },
            {
              key: 'source',
              label: 'منشأ',
              value: report.source === 'PROVISIONING' ? 'ثبت خریدار' : 'اصلاح اعتبار',
            },
          ]}
        />
      );
    case 'CREDIT_ALLOCATION_ADJUSTMENT':
      return (
        <FactList
          items={[
            {
              key: 'limit',
              label: 'سقف تخصیص',
              value: change(money(report.currentLimitIrr), money(report.newLimitIrr)),
            },
            { key: 'delta', label: 'تغییر سقف', value: <Money rials={report.deltaIrr} /> },
            ...(report.programExposure === null
              ? []
              : [
                  {
                    key: 'exposure',
                    label: 'تعهد برنامه',
                    value: (
                      <StoredFields
                        fields={{
                          afterIrr: report.programExposure.afterIrr,
                          capIrr: report.programExposure.capIrr,
                          committedIrr: report.programExposure.committedIrr,
                        }}
                      />
                    ),
                  },
                ]),
          ]}
        />
      );
    case 'PAYOUT_ACCOUNT_CHANGE':
      return (
        <FactList
          items={[
            { key: 'owner', label: 'صاحب حساب تسویه', value: report.ownerDisplayNameFa },
            {
              key: 'current',
              label: 'حساب فعلی',
              value:
                report.currentAccount === null ? (
                  'ندارد'
                ) : (
                  <span className="admin-inline">
                    <Masked kind="sheba" value={report.currentAccount.shebaMasked} />
                    {` — ${report.currentAccount.holderName}`}
                  </span>
                ),
            },
            {
              key: 'new',
              label: 'حساب جدید',
              value: (
                <span className="admin-inline">
                  <Masked kind="sheba" value={report.newShebaMasked} />
                  {` — ${report.newHolderName}`}
                </span>
              ),
            },
          ]}
        />
      );
    case 'PLATFORM_ROLE_GRANT':
      return <RoleGrantImpact report={report} />;
    case 'FINANCIAL_ADJUSTMENT':
      return <FinancialAdjustmentImpact report={report} />;
    case 'CREDIT_PROGRAM_ACTIVATION': {
      const activation = report as typeof report & {
        readonly dedicatedOrganizationNameFa?: string;
        readonly programNameFa?: string;
      };
      const programLabel =
        activation.programNameFa ?? formatProgramCodeRevision(report.programCode, report.revision);
      const organizationLabel =
        report.dedicatedOrganizationId === null
          ? 'ندارد'
          : (activation.dedicatedOrganizationNameFa ?? 'سازمان اختصاصی');
      return (
        <FactList
          items={[
            {
              key: 'program',
              label: 'برنامه',
              value: programLabel,
            },
            {
              key: 'amount',
              label: 'مبلغ پیش‌فرض هر نفر',
              value: <Money rials={report.defaultAmountIrr} />,
            },
            {
              key: 'cap',
              label: 'سقف کل تعهد',
              value: <Money rials={report.totalExposureCapIrr} />,
            },
            {
              key: 'validity',
              label: 'اعتبار زمانی',
              value: validityPolicyText(report.validityPolicy),
            },
            {
              key: 'organization',
              label: 'سازمان اختصاصی',
              value: organizationLabel,
            },
            {
              key: 'supersedes',
              label: 'جایگزین نسخهٔ فعال',
              value: report.supersedesProgramId === null ? 'اولین نسخهٔ فعال' : 'بله',
            },
          ]}
        />
      );
    }
    case 'CREDIT_PROGRAM_TARGETING_RUN':
      // The run's snapshot is the impact report (AC §18.9.1): the server stores it spread here.
      return 'recipientCount' in report ? (
        <SnapshotFacts snapshot={report as unknown as TargetingSnapshot} />
      ) : (
        <StoredFields fields={report as Readonly<Record<string, unknown>>} />
      );
    default:
      return <StoredFields fields={report as Readonly<Record<string, unknown>>} />;
  }
}

/** Approval row status (XAP-16): no extra «قابل تصمیم» chip; deciders use the inbox filter. */
export function ApprovalStatus({
  status,
}: {
  readonly decidable?: boolean;
  readonly status: string;
}) {
  return <AbnormalStatusChip enumName="approvalRequestStatus" value={status} />;
}
